import * as zod from 'zod';

import { rotateVectorByQuaternion } from '@sb/geometry';
import { CollisionBoxConfig } from '@sb/integrations/types/CollisionBox';

import { OR2FG7_DEFAULT_COLLISION_BOX } from '../../OnRobot2FG7/constants';
import { OR3FG15_DEFAULT_COLLISION_BOX } from '../../OnRobot3FG15/constants';
import { OnRobotQuickChangerCompatibleGripperConfiguration as OnRobotDualQuickChangerCompatibleGripper } from '../../shared/OnRobotQuickChanger';
import { OR_DUAL_QUCK_CHANGER_SHORTNAME } from '../constants';

export const OnRobotDualQuickChangerConfigurationBase = zod.object({
  kind: zod.literal('OnRobotDualQuickChanger'),
  name: zod.string().default(OR_DUAL_QUCK_CHANGER_SHORTNAME),
  grippers: zod.object({
    primary: OnRobotDualQuickChangerCompatibleGripper,
    secondary: OnRobotDualQuickChangerCompatibleGripper,
  }),
  mountOrientation: zod.number().default(0),
  primaryCollisionBox: CollisionBoxConfig.optional(),
  secondaryCollisionBox: CollisionBoxConfig.optional(),
});

export const OnRobotDualQuickChangerConfiguration =
  OnRobotDualQuickChangerConfigurationBase.transform((cfg) => {
    const computeDefaultBox = (
      gripper:
        | (typeof cfg)['grippers']['primary']
        | (typeof cfg)['grippers']['secondary'],
    ) => {
      if (!gripper) return undefined;
      let size: { x: number; y: number; z: number } | undefined;
      if (gripper.kind === 'OnRobot2FG7')
        size = OR2FG7_DEFAULT_COLLISION_BOX.size;
      else if (gripper.kind === 'OnRobot3FG15')
        size = OR3FG15_DEFAULT_COLLISION_BOX.size;
      else return undefined;

      const tcp = (gripper as any).tcpTransform as
        | {
            x?: number;
            y?: number;
            z?: number;
            i?: number;
            j?: number;
            k?: number;
            w?: number;
          }
        | undefined;

      if (!tcp) return undefined;

      const halfHeight = size.z / 2;
      const local = { x: 0, y: 0, z: -halfHeight };
      const q = { i: tcp.i ?? 0, j: tcp.j ?? 0, k: tcp.k ?? 0, w: tcp.w ?? 1 };
      const rotated = rotateVectorByQuaternion(local, q);

      const origin = {
        x: (tcp.x ?? 0) + rotated.x,
        y: (tcp.y ?? 0) + rotated.y,
        z: (tcp.z ?? 0) + rotated.z,
      };

      return { origin, size };
    };

    const primaryCollisionBox = computeDefaultBox(cfg.grippers.primary);

    const secondaryCollisionBox = computeDefaultBox(cfg.grippers.secondary);

    return { ...cfg, primaryCollisionBox, secondaryCollisionBox };
  });

export type OnRobotDualQuickChangerConfiguration = zod.infer<
  typeof OnRobotDualQuickChangerConfiguration
>;

export type OnRobotDualQuickChangerConfigurationBaseType = zod.infer<
  typeof OnRobotDualQuickChangerConfigurationBase
>;
